// Local MIRROR of @alchmystudio/config's unit helpers (pxToRem, remToPx, ROOT_PX).
// The app does NOT depend on the (private) package — importing it would break the
// app's `npm ci`/CI (see tokenSchema.ts for the same reasoning). So we mirror them
// here and keep them in sync with @alchmystudio/config.
//
// ROOT_PX is the SINGLE source of the base in-app: never hardcode 16 anywhere else
// — read it from here so a base change is one edit. The Brand-kit editor shows
// designers PIXELS; tokens are always STORED in rem (via pxToRem on save) and shown
// back in px (via remToPx on display).

export const ROOT_PX = 16;

export function pxToRem(px: number, base: number = ROOT_PX): number {
  return px / base;
}

export function remToPx(rem: number, base: number = ROOT_PX): number {
  return rem * base;
}

// Trim float noise (0.1 + 0.2 etc.) and any trailing zeros so values read cleanly.
function tidy(n: number): string {
  return parseFloat(n.toFixed(4)).toString();
}

// --- CSS length-string helpers (token <-> editor field) ---------------------
//
// Token values are CSS length strings ('0.5rem', '0.75rem 1.5rem'). The editor
// works in px. These convert one whitespace-separated value at a time and leave
// anything that isn't a plain rem/px length untouched: '0', '50%', '100vw',
// keywords, var(...) all pass through unchanged.

// rem (stored) -> px (display), per token.
function tokenRemToPx(token: string): string {
  const m = /^(-?\d*\.?\d+)rem$/.exec(token);
  if (!m) return token;
  return `${tidy(remToPx(parseFloat(m[1])))}px`;
}

// px or bare number (typed) -> rem (stored), per token. A bare number is treated
// as px so designers can type "12" or "12px". Zero stays unitless '0'.
function tokenPxToRem(token: string): string {
  const m = /^(-?\d*\.?\d+)(px)?$/.exec(token);
  if (!m) return token;
  const px = parseFloat(m[1]);
  if (px === 0) return '0';
  return `${tidy(pxToRem(px))}rem`;
}

function mapTokens(value: string, fn: (t: string) => string): string {
  const t = value.trim();
  if (t === '') return '';
  return t.split(/\s+/).map(fn).join(' ');
}

// Whole-field conversions (handle shorthand like padding's 1-4 values).
export function fieldRemToPx(value: string): string {
  return mapTokens(value, tokenRemToPx);
}

export function fieldPxToRem(value: string): string {
  return mapTokens(value, tokenPxToRem);
}

// Parse a single CSS length to a px NUMBER (rem via ROOT_PX, px/bare-number as-is).
// Returns undefined for anything that isn't a plain rem/px/number length. Used by
// the token migration to flatten old rem/SpaceValue grid + padding to px anchors.
export function lengthToPx(value: string): number | undefined {
  const t = value.trim();
  const rem = /^(-?\d*\.?\d+)rem$/.exec(t);
  if (rem) return remToPx(parseFloat(rem[1]));
  const px = /^(-?\d*\.?\d+)(px)?$/.exec(t);
  if (px) return parseFloat(px[1]);
  return undefined;
}
